import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { SlidersHorizontal, X } from "lucide-react";
import { http } from "../lib/api";
import ProductCard from "../components/ProductCard";
import { Skeleton } from "../components/ui/skeleton";
import { Sheet, SheetContent, SheetTrigger } from "../components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { inr } from "../lib/format";

const SIZES = ["XS", "S", "M", "L", "XL", "XXL"];
const SORTS = [
  { v: "featured", l: "Featured" }, { v: "newest", l: "Newest" },
  { v: "price_asc", l: "Price: Low to High" }, { v: "price_desc", l: "Price: High to Low" },
  { v: "bestsellers", l: "Best Sellers" },
];

function FilterPanel({ sp, setParam, cats, cols, clearAll }) {
  const Group = ({ title, children }) => (
    <div className="border-b border-zinc-900 py-5">
      <p className="text-[11px] uppercase tracking-widest text-zinc-400 font-semibold mb-3">{title}</p>
      {children}
    </div>
  );
  return (
    <div className="text-sm">
      <div className="flex items-center justify-between pb-2">
        <p className="font-display text-2xl text-white tracking-wide">Filters</p>
        <button onClick={clearAll} className="text-xs text-accent uppercase tracking-wider" data-testid="clear-filters">Clear</button>
      </div>
      <Group title="Category">
        <div className="space-y-2">
          {cats.filter((c) => !c.parent_id).map((c) => (
            <button key={c.id} onClick={() => setParam("category", sp.get("category") === c.slug ? "" : c.slug)}
              className={`block text-left ${sp.get("category") === c.slug ? "text-accent" : "text-zinc-400 hover:text-white"}`}
              data-testid={`filter-cat-${c.slug}`}>{c.name}</button>
          ))}
          {cats.filter((c) => c.parent_id).map((c) => (
            <button key={c.id} onClick={() => setParam("category", sp.get("category") === c.slug ? "" : c.slug)}
              className={`block text-left pl-3 text-xs ${sp.get("category") === c.slug ? "text-accent" : "text-zinc-500 hover:text-white"}`}>
              {c.name}</button>
          ))}
        </div>
      </Group>
      <Group title="Collection">
        <div className="space-y-2">
          {cols.map((c) => (
            <button key={c.id} onClick={() => setParam("collection", sp.get("collection") === c.slug ? "" : c.slug)}
              className={`block text-left ${sp.get("collection") === c.slug ? "text-accent" : "text-zinc-400 hover:text-white"}`}>{c.name}</button>
          ))}
        </div>
      </Group>
      <Group title="Size">
        <div className="flex flex-wrap gap-2">
          {SIZES.map((s) => (
            <button key={s} onClick={() => setParam("size", sp.get("size") === s ? "" : s)}
              className={`w-10 h-10 border text-xs ${sp.get("size") === s ? "border-accent-ss text-accent" : "border-zinc-800 text-zinc-400 hover:border-zinc-600"}`}
              data-testid={`filter-size-${s}`}>{s}</button>
          ))}
        </div>
      </Group>
      <Group title="Price">
        <Select value={sp.get("max_price") || "all"} onValueChange={(v) => setParam("max_price", v === "all" ? "" : v)}>
          <SelectTrigger className="bg-zinc-900 border-zinc-800"><SelectValue placeholder="Any" /></SelectTrigger>
          <SelectContent className="bg-zinc-900 border-zinc-800">
            <SelectItem value="all">Any price</SelectItem>
            <SelectItem value="1500">Under ₹1,500</SelectItem>
            <SelectItem value="3000">Under ₹3,000</SelectItem>
            <SelectItem value="5000">Under ₹5,000</SelectItem>
          </SelectContent>
        </Select>
      </Group>
      <Group title="Availability">
        <label className="flex items-center gap-2 text-zinc-400 cursor-pointer">
          <input type="checkbox" checked={sp.get("on_sale") === "1"} onChange={(e) => setParam("on_sale", e.target.checked ? "1" : "")} />
          On Sale
        </label>
      </Group>
    </div>
  );
}

export default function Shop({ title = "Shop All", fixedCollection, fixedCategory, hideFilters }) {
  const [sp, setSp] = useSearchParams();
  const [data, setData] = useState(null);
  const [cats, setCats] = useState([]);
  const [cols, setCols] = useState([]);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    http.get("/categories").then((r) => setCats(r.data)).catch(() => {});
    http.get("/collections").then((r) => setCols(r.data)).catch(() => {});
  }, []);

  const setParam = useCallback((k, v) => {
    const n = new URLSearchParams(sp);
    if (v) n.set(k, v); else n.delete(k);
    setSp(n);
  }, [sp, setSp]);

  useEffect(() => {
    const q = new URLSearchParams(sp);
    if (fixedCollection) q.set("collection", fixedCollection);
    if (fixedCategory) q.set("category", fixedCategory);
    q.set("limit", "48");
    setData(null);
    http.get(`/products?${q.toString()}`).then((r) => setData(r.data)).catch(() => setData({ items: [], total: 0 }));
  }, [sp, fixedCollection, fixedCategory]);

  const sort = sp.get("sort") || "featured";
  const chips = ["category", "collection", "size", "max_price", "on_sale"]
    .filter((k) => sp.get(k)).map((k) => ({ k, v: sp.get(k) }));

  return (
    <div className="max-w-7xl mx-auto px-4 lg:px-8 py-8">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
        {title ? <h1 className="font-display text-4xl sm:text-6xl text-white uppercase tracking-tight">{title}</h1> : null}
        <div className="flex items-center gap-3 ml-auto">
          {!hideFilters && (
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <button className="lg:hidden flex items-center gap-2 border border-zinc-800 px-4 py-2.5 text-xs uppercase tracking-wider" data-testid="mobile-filter-trigger">
                  <SlidersHorizontal size={15} /> Filters
                </button>
              </SheetTrigger>
              <SheetContent side="left" className="bg-[#070708] border-zinc-800 overflow-y-auto">
                <FilterPanel sp={sp} setParam={setParam} cats={cats} cols={cols} clearAll={() => setSp({})} />
              </SheetContent>
            </Sheet>
          )}
          <Select value={sort} onValueChange={(v) => setParam("sort", v)}>
            <SelectTrigger className="w-[190px] bg-zinc-900 border-zinc-800" data-testid="sort-select"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-zinc-900 border-zinc-800">
              {SORTS.map((s) => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {chips.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-6">
          {chips.map((c) => (
            <button key={c.k} onClick={() => setParam(c.k, "")} className="flex items-center gap-1.5 bg-zinc-900 border border-zinc-800 px-3 py-1.5 text-xs text-zinc-300"
              data-testid={`chip-${c.k}`}>
              {c.k === "max_price" ? `Under ${inr(c.v)}` : c.k === "on_sale" ? "On Sale" : c.v} <X size={12} />
            </button>
          ))}
        </div>
      )}

      <div className="flex gap-10">
        {!hideFilters && (
          <aside className="hidden lg:block w-60 shrink-0">
            <FilterPanel sp={sp} setParam={setParam} cats={cats} cols={cols} clearAll={() => setSp({})} />
          </aside>
        )}
        <div className="flex-1">
          {!data ? (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 sm:gap-6">
              {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="aspect-[3/4] bg-zinc-900" />)}
            </div>
          ) : data.items.length === 0 ? (
            <p className="text-zinc-500 py-20 text-center">No products match your filters.</p>
          ) : (
            <>
              <p className="text-xs text-zinc-500 mb-4">{data.total} products</p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 sm:gap-6">
                {data.items.map((p, i) => <ProductCard key={p.id} p={p} index={i} />)}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
